import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createPromo, updatePromo } from "../../services/promoFn";
import CustomSnackbar from "../../components/customSnackBar/customSnackBar";
import AppLoader from "../../components/loader/loader";

const PromoForm = ({
  open,
  onClose,
  formData,
  setFormData,
  editData,
  refreshList,
}) => {
  const [loading, setLoading] = useState(false);
  const snackRef = useRef();

  useEffect(() => {
    if (editData) {
      setFormData(editData.formData);
    } else {
      setFormData({
        title: "",
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        startDate: "",
        endDate: "",
        isActive: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const openSnack = (type, message) => {
    snackRef.current?.isOpenSnack({ snackType: type, snackMessage: message });
  };

  const handleSubmit = async () => {
    // basic validation
    if (
      !formData.title ||
      !formData.code ||
      !formData.discountValue ||
      !formData.startDate ||
      !formData.endDate
    ) {
      openSnack("error", "Please fill required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        code: formData.code,
        description: formData.description,
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: !!formData.isActive,
      };

      const response = editData?._id
        ? await updatePromo(payload, editData._id)
        : await createPromo(payload);

      if (response?.data?.status === "success") {
        openSnack(
          "success",
          response.data.message || (editData?._id ? "Updated" : "Created")
        );
        onClose(true);
        refreshList?.();
      } else {
        openSnack("error", response?.data?.message || "Failed to save promo");
      }
    } catch (error) {
      console.error("Promo save error:", error);
      openSnack("error", error?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={loading} />
      <Box>
        <Dialog
          open={open}
          onClose={() => onClose(false)}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 420 } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              p: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography
              sx={{ color: "primary.main", fontWeight: 600, fontSize: 20 }}
            >
              {editData?._id ? "Edit Promo" : "Add Promo"}
            </Typography>
            <IconButton onClick={() => onClose(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                fullWidth
                disabled={loading}
              />

              <TextField
                label="Code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                fullWidth
                disabled={loading}
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
                disabled={loading}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  label="Discount Type"
                  value={formData.discountType}
                  onChange={(e) =>
                    setFormData({ ...formData, discountType: e.target.value })
                  }
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="flat">Flat</MenuItem>
                </TextField>

                <TextField
                  label="Discount Value"
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) =>
                    setFormData({ ...formData, discountValue: e.target.value })
                  }
                  sx={{ minWidth: 160 }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={
                    formData.startDate
                      ? formData.startDate.split("T")[0]
                      : formData.startDate
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={
                    formData.endDate
                      ? formData.endDate.split("T")[0]
                      : formData.endDate
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={() => onClose(false)}
              disabled={loading}
              sx={{ width: 90 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              variant="contained"
              sx={{ width: 110 }}
            >
              {editData?._id ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <CustomSnackbar ref={snackRef} />
      </Box>
    </>
  );
};

export default PromoForm;
